import { loadScript } from './loadScript';
import { getFromLocalStorage } from './localStorage';
import { API_BASE_URL } from '../config';

// Initialize Cashfree SDK
const initializeCashfree = async () => {
  try {
    await loadScript('https://sdk.cashfree.com/js/v3/cashfree.js');
    return window.Cashfree({
      mode: 'sandbox' // Change to 'production' for live
    });
  } catch (error) {
    console.error('Failed to load Cashfree SDK:', error);
    throw error;
  }
};

// Create a payment order via our backend
const createCashfreeOrder = async (orderData) => {
  const token = getFromLocalStorage('token');
  const user = getFromLocalStorage('user');
  
  const response = await fetch(`${API_BASE_URL}/api/payments/cashfree/initiate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'User-Info': user?.emailId || user?.username || ''
    },
    body: JSON.stringify(orderData)
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'Failed to create order');
  }
  
  return data.data;
};

// Process payment using Cashfree
const processCashfreePayment = async (card, onSuccess, onError) => {
  try {
    // Get streamer username from URL if available
    const urlParams = new URLSearchParams(window.location.search);
    const streamerUsername = urlParams.get('username') || card.username || card.streamerUsername;
    // Detect purchase origin based on current path
    const path = (typeof window !== 'undefined' && window.location && window.location.pathname) ? window.location.pathname : '';
    const purchaseOrigin = path.includes('/streamers') ? 'streamer' : (path.includes('/dashboard') ? 'dashboard' : 'unknown');
    
    // Enhance card data with streamer username
    const enhancedCard = {
      ...card,
      streamerUsername: streamerUsername,
      purchaseOrigin
    };
    
    // Store card data in BOTH window object AND sessionStorage
    window.__pendingCardPurchase = enhancedCard;
    sessionStorage.setItem('pendingCardPurchase', JSON.stringify(enhancedCard));
    console.log('💾 Stored card with streamer username:', streamerUsername);
    console.log('💾 Stored card in sessionStorage');
    
    const cashfree = await initializeCashfree();
    const user = getFromLocalStorage('user') || {};
    
    const orderData = {
      order_amount: card.price,
      order_currency: 'INR',
      order_note: `Purchase ${card.cardTitle}`,
      customer_details: {
        customer_id: user._id || 'guest',
        customer_phone: user.phone || '9999999999',
        customer_email: user.emailId || 'guest@example.com',
        customer_name: user.username || 'Guest'
      },
      order_meta: {
        return_url: `${window.location.origin}/payment-callback?status={order_status}&order_id={order_id}`
      }
    };

    // Create order and get payment session
    const { order_id, payment_session_id } = await createCashfreeOrder(orderData);
    
    // Configure checkout options
    const checkoutOptions = {
      paymentSessionId: payment_session_id,
      returnUrl: `${window.location.origin}/payment-callback?order_id=${order_id}&origin=${encodeURIComponent(purchaseOrigin)}`,
      redirectTarget: '_self',
      theme: {
        logo: `${window.location.origin}/logo192.png`,
        buttonColor: '#F59E0B',
        buttonTextColor: '#000000',
        buttonRadius: '4px',
        themeColor: '#F59E0B'
      }
    };

    // Open Cashfree checkout
    console.log('🚀 Opening Cashfree checkout with options:', checkoutOptions);
    const result = cashfree.checkout(checkoutOptions);
    console.log('📱 Cashfree checkout result:', result);
  } catch (error) {
    console.error('❌ ========== PAYMENT INITIALIZATION ERROR ==========');
    console.error('Error:', error);
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);
    onError({ message: error.message || 'Payment initialization failed' });
  }
};

// Verify payment status with our backend
const verifyPayment = async (orderId) => {
  const token = getFromLocalStorage('token');
  const user = getFromLocalStorage('user');
  
  const response = await fetch(`${API_BASE_URL}/api/payments/cashfree/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'User-Info': user?.emailId || user?.username || ''
    },
    body: JSON.stringify({ order_id: orderId })
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'Payment verification failed');
  }
  
  return data.data;
};

export { processCashfreePayment };
